import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../Layouts/context/Authcontext';
import api from '../../../../api';
import ReactApexChart from 'react-apexcharts';
import { TailSpin } from 'react-loader-spinner';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

function TopLeakageUsers() {
  const [cardData, setCardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const { accessToken } = useAuth();

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      let baseUrls = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrls}/managerDashboard/getTop5`, {
        params: { month, year },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.data.success) {
        setCardData(response.data.data.topLeakageUsers);
      } else {
        setError('Data fetch unsuccessful');
      }
    } catch (err) {
      setError('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [accessToken, month, year]);

  const handleMonthChange = (e) => setMonth(parseInt(e.target.value));
  const handleYearChange = (e) => setYear(parseInt(e.target.value));

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthOptions = monthNames.map((name, index) => (
    <option key={index + 1} value={index + 1}>{name}</option>
  ));

  const currentYear = new Date().getFullYear();
  const yearOptions = [...Array(5).keys()].map(i => {
    const y = currentYear - i;
    return <option key={y} value={y}>{y}</option>;
  });

  const topLeakageUsers = (cardData || [])
    .filter(user => user.leakageHours !== undefined)
    .sort((a, b) => b.leakageHours - a.leakageHours)
    .slice(0, 5);

  const series = [{
    name: 'Leakage Hours',
    data: topLeakageUsers.map(user => ({
      x: user.employeeId?.toString() || 'Unknown',
      y: parseFloat(user.leakageHours.toFixed(2)),
      employeeName: user.employeeName,
    })),
  }];

  const options = {
    chart: { type: 'bar', toolbar: { show: false }, animations: { easing: 'easeinout', speed: 500 } },
    plotOptions: { bar: { horizontal: false, borderRadius: 6, columnWidth: '45%' } },
    dataLabels: { enabled: true, style: { fontSize: '12px', colors: ['#fff'] }, offsetY: -8, formatter: val => val.toFixed(2) },
    colors: ['#3b82f6'],
    xaxis: { type: 'category', labels: { style: { fontSize: '12px', fontWeight: 600, colors: ['#374151'] } }, title: { text: 'Employees' } },
    yaxis: { title: { text: 'Leakage Hrs', style: { fontSize: '14px', fontWeight: 700 } }, labels: { formatter: val => val.toFixed(2) } },
    tooltip: {
      x: { show: false },
      y: {
        formatter: (val, { seriesIndex, dataPointIndex, w }) => {
          const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
          return `${data.employeeName}: ${val.toFixed(2)} Hrs`;
        },
      },
    },
    title: { text: 'Top 5 Employees Leakage Hours', align: 'center', style: { fontSize: '16px', fontWeight: '600', color: '#1f2937' } },
    responsive: [
      { breakpoint: 768, options: { plotOptions: { bar: { columnWidth: '60%' } }, dataLabels: { style: { fontSize: '11px' } }, xaxis: { labels: { style: { fontSize: '10px' } } } } },
      { breakpoint: 480, options: { plotOptions: { bar: { columnWidth: '70%' } }, dataLabels: { style: { fontSize: '10px' } }, xaxis: { labels: { style: { fontSize: '9px' } } } } },
    ],
  };

  return (
    <div className="bg-white shadow rounded-lg p-6 transition-transform hover:scale-[1.02] flex flex-col">
      {/* Header with filters */}
      <div className="flex justify-between items-center mb-4 bg-gray-100 rounded-md p-3">
        <h2 className="text-lg font-semibold text-gray-700">Top Leakage Users</h2>
        <div className="flex gap-2">
          <select value={month} onChange={handleMonthChange} className="border rounded px-2 py-1 text-sm bg-white shadow-sm focus:outline-none">
            {monthOptions}
          </select>
          <select value={year} onChange={handleYearChange} className="border rounded px-2 py-1 text-sm bg-white shadow-sm focus:outline-none">
            {yearOptions}
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="relative w-full" style={{ minHeight: '350px' }}>
        {loading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <TailSpin height="50" width="50" color="#3b82f6" ariaLabel="loading" />
            <p className="text-gray-500 mt-2 text-sm">Loading data...</p>
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center text-red-500 text-sm">{error}</div>
        ) : cardData.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-base">No data available for selected month</div>
        ) : (
          <ReactApexChart options={options} series={series} type="bar" height="350" width="100%" />
        )}
      </div>
    </div>
  );
}

export default TopLeakageUsers;
